// useAiImportJson — parsa AI-emitted workflow-JSON (lean / compact /
// multi-sheet / single-sheet) och route till stager (credit-gated) eller
// direkt import. Extraherad ur AiAssistantDialog (#1 i
// PRE_RELEASE_REVIEW_100).

import { useCallback, type RefObject } from 'react';
import { toast } from '@/hooks/use-toast';
import { parseWorkflowState, parseMultiSheetState, getWorkflowValidationErrors } from '@/lib/workflowSchema';
import {
  isMultiSheetState,
  extractSheetState,
  type WorkflowState,
  type MultiSheetWorkflowState,
} from '@/types/workflow';
import { isCompactFormat, expandCompactWorkflow, normaliseLeanFormat } from '@/lib/compactWorkflow';
import { DEFAULT_INTENT, type Intent, type IntentClassification } from '@/lib/intentTypes';

interface UseAiImportJsonArgs {
  onImport: (state: WorkflowState | MultiSheetWorkflowState, overLimit?: boolean) => void;
  onStageAiProposal?: (
    state: WorkflowState,
    meta?: { conversationId?: string; intent?: Intent; promptMessageId?: string },
  ) => void;
  onOpenChange: (open: boolean) => void;
  conversationIdRef: RefObject<string | null>;
  /** Senaste auto-klassificering så Apply kan skapa artifact_version
   *  med rätt intent (I-001). */
  lastAutoClassification: IntentClassification | null;
}

export function useAiImportJson({
  onImport,
  onStageAiProposal,
  onOpenChange,
  conversationIdRef,
  lastAutoClassification,
}: UseAiImportJsonArgs) {
  return useCallback((jsonStr: string) => {
    try {
      let parsed = JSON.parse(jsonStr);
      // Lean format first (flat block.name + connector.from/to) — AI
      // occasionally invents this despite the prompt. Normalising
      // gives us the standard shape, which then flows through the
      // compact-expander and zod parsers below.
      parsed = normaliseLeanFormat(parsed);
      if (isCompactFormat(parsed)) parsed = expandCompactWorkflow(parsed);

      // Try multi-sheet first when the expanded shape carries `sheets`
      // — the AI sometimes emits multi-sheet flows for "phases" /
      // "layers" requests and parseWorkflowState (single-sheet)
      // would otherwise reject the whole document. We pick the
      // active sheet for the legacy WorkflowState shape that the
      // canvas consumer expects.
      let state: WorkflowState | null = null;
      if (isMultiSheetState(parsed)) {
        const multi = parseMultiSheetState(parsed);
        if (multi) {
          state = extractSheetState(multi as MultiSheetWorkflowState, multi.activeSheetIndex ?? 0);
        }
      }
      if (!state) {
        state = parseWorkflowState(parsed) as WorkflowState | null;
      }

      if (state) {
        // Credit-gated: stage som preview-layer och visa kostnad.
        // Direkt import fallback bara om caller INTE gett oss en stager
        // (inte skarpt läge). I-001: skicka conversationId + senaste
        // klassificerade intent så Apply kan skapa artifact_version.
        if (onStageAiProposal) {
          const meta = {
            conversationId: conversationIdRef.current ?? undefined,
            intent: lastAutoClassification?.intent ?? DEFAULT_INTENT,
          };
          onStageAiProposal(state as WorkflowState, meta);
          onOpenChange(false);
          toast({ title: 'Preview on canvas', description: `${state.blocks.length} blocks queued for approval` });
        } else {
          onImport(state as WorkflowState);
          onOpenChange(false);
          toast({ title: 'Imported', description: `${state.blocks.length} blocks imported to canvas.` });
        }
      } else {
        // Surface the specific validation issue so users know what
        // to ask the AI to fix. Previously the toast said only
        // "Could not validate workflow structure" with no detail.
        const issues = getWorkflowValidationErrors(parsed) ?? [];
        const headline = issues[0] ?? 'unknown shape';
        const more = issues.length > 1 ? ` (+${issues.length - 1} more)` : '';
        toast({
          title: 'Invalid workflow JSON',
          description: `${headline}${more}`,
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'JSON parse error',
        description: err instanceof Error ? err.message : 'Could not parse JSON data.',
        variant: 'destructive',
      });
    }
  }, [onImport, onStageAiProposal, onOpenChange, lastAutoClassification, conversationIdRef]);
}
